// src/utils/errorTracker.js
// Sistema de tracking de errores para métricas de plataforma

import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';

/**
 * Registra un error en la plataforma para métricas
 * @param {Error|string} error - El error a registrar
 * @param {Object} context - Contexto adicional (url, userAgent, etc.)
 */
export const logPlatformError = async (error, context = {}) => {
  try {
    const errorData = {
      tipo: error.name || 'UnknownError',
      mensaje: error.message || String(error),
      stack: error.stack || '',
      url: context.url || (typeof window !== 'undefined' ? window.location.href : ''),
      userAgent: context.userAgent || (typeof window !== 'undefined' ? window.navigator.userAgent : ''),
      userId: context.userId || null,
      severidad: context.severidad || 'medium', // low, medium, high, critical
    };

    const logError = httpsCallable(functions, 'logPlatformError');
    await logError(errorData);

    // Integración con Firebase Crashlytics para Móvil
    if (Capacitor.isNativePlatform()) {
      await FirebaseCrashlytics.addLogMessage({ message: `Error en plataforma: ${errorData.mensaje}` });

      if (context.userId) {
        await FirebaseCrashlytics.setUserId({ userId: context.userId });
      }

      await FirebaseCrashlytics.recordException({
        message: errorData.mensaje,
        stacktrace: errorData.stack || 'No stacktrace available'
      });
    }
  } catch (logError) {
    // Si falla el logging, no queremos que rompa la app
    console.error('Error al registrar error en plataforma:', logError);
  }
};

/**
 * Configura el error handler global para la aplicación
 */
export const setupGlobalErrorHandling = () => {
  if (typeof window === 'undefined') return;

  // Capturar errores no manejados
  window.addEventListener('error', (event) => {
    const errorMsg = String(event.message || event.error?.message || '');
    if (errorMsg.includes('INTERNAL ASSERTION FAILED')) {
      console.warn('⚠️ [Firestore] Aserción interna interceptada:', errorMsg);
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return;
    }

    logPlatformError(event.error || new Error(event.message), {
      url: event.filename,
      severidad: 'high',
    });
  });

  // Capturar promesas rechazadas no manejadas
  window.addEventListener('unhandledrejection', (event) => {
    const reasonMsg = String(event.reason?.message || event.reason || '');
    if (reasonMsg.includes('INTERNAL ASSERTION FAILED')) {
      console.warn('⚠️ [Firestore] Promesa rechazada por aserción interna interceptada:', reasonMsg);
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return;
    }

    logPlatformError(
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason)),
      {
        severidad: 'medium',
      }
    );
  });
};

/**
 * Wrapper para funciones async que captura errores automáticamente
 */
export const withErrorTracking = (fn, context = {}) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      await logPlatformError(error, context);
      throw error; // Re-lanzar el error para que el código pueda manejarlo
    }
  };
};
